import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatCheckbox, MatCheckboxDefaultOptions, MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { ISearchFilterSettings } from '@shared/models/isearch-filter-settings.model';
import { IOption } from '@shared/models/icommons.model';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { connectFilters, addCheckedProperty, cleanUserFilters } from './more-filters.helpers';
import { MoreFiltersData, MoreFiltersOptions } from './more-filters.model';
import { map, Observable, of } from 'rxjs';
import {
  MORE_FILTERS_CUSTOM_OPTIONS_TOKEN,
  MoreFiltersCustomOptionsService,
  isImplementingMoreFiltersCustomOptionsService,
} from './more-filters-custom-options.service';
import { SectionOptionsSettings } from './section-options.component';

import { EntityType } from '@shared/models/entity-enum';
import { CHECKBOX_SERVICE_TOKEN, CheckboxService, CheckboxState } from '../../services/checkbox-state.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PropertySections } from '@miq/catalog';
import { UsageTypes } from '@core/composers/usages/usage';
import { getPropertyTypeLabel } from '@miq/utils';

const MAX_NUM_FILTERS = 20;

@UntilDestroy()
@Component({
  selector: 'miq-more-filters',
  templateUrl: './more-filters.component.html',
  styleUrls: ['./more-filters.component.scss'],
  providers: [
    { provide: MAT_CHECKBOX_DEFAULT_OPTIONS, useValue: { clickAction: 'noop' } as MatCheckboxDefaultOptions },
  ],
  standalone: false,
})
export class MoreFiltersComponent implements OnChanges, OnDestroy {
  @Input() dataFilters: MoreFiltersData;
  @Input() isModal: boolean;
  @Input() isClipboard: boolean;
  @Input() overrideDisable?: (context: { filterKey: number }) => boolean;
  @Output() filtersChange = new EventEmitter(null);

  defaultData: MoreFiltersData = {
    options: {
      title: '',
      subTitle: '',
      message: '',
    },
    userFilters: [],
    filtersCatalog: [],
    filterForRecords: false,
    filterForTableView: false,
    defaultFilters: [],
    isFilter: false,
    isCatalogData: false,
    key: '',
  };
  data: MoreFiltersData;

  public filtersTitle: string;
  public dialogOptions: MoreFiltersOptions;
  public pinnedFiltersCatalog: ISearchFilterSettings[];
  public filtersCatalog: ISearchFilterSettings[];
  public userFilters: ISearchFilterSettings[];
  public defaultColumns: ISearchFilterSettings[];
  public searchFormControl: UntypedFormControl;
  public isFilterDialog = false;
  public mainHeader = null;
  public entity: EntityType;
  public customFilters: ISearchFilterSettings;

  private forReset: ISearchFilterSettings[];
  private fullCatalog: ISearchFilterSettings[];

  filterForRecords = false;
  filterForTableView = false;
  isCatalogData = false;
  checkboxState$: Observable<CheckboxState>;
  customOptions$: Observable<SectionOptionsSettings[]>;
  hasCustomOptions = false;
  customOptionsError = false;
  showPropertyData = false;
  showAllPropertyData = false;
  checkboxState: CheckboxState;
  isIndustrialChecked = false;
  isRetailChecked = false;
  isLandChecked = false;

  constructor(
    private dialogRef: MatDialogRef<MoreFiltersComponent>,
    private formBuilder: UntypedFormBuilder,
    @Optional()
    @Inject(MORE_FILTERS_CUSTOM_OPTIONS_TOKEN)
    private customOptiosnService?: MoreFiltersCustomOptionsService,
    @Optional()
    @Inject(CHECKBOX_SERVICE_TOKEN)
    private checkboxService?: CheckboxService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.dataFilters || changes.isClipboard) {
      this.initializeFilters();
    }
  }

  private initializeFilters(): void {
    if (!this.dataFilters) {
      return;
    }
    this.data = this.dataFilters;
    this.setFiltersData({ ...this.defaultData, ...this.dataFilters });
  }

  setFiltersData(data: MoreFiltersData): void {
    const {
      options,
      userFilters,
      filtersCatalog,
      filterForRecords,
      filterForTableView,
      defaultFilters,
      isFilter,
      isCatalogData,
      customOptionsService,
      mainHeader,
      checkboxService,
      key,
    } = data;
    let pinnedFilters = data?.pinnedFilters;

    // Override the CustomOptiosnService, this is when is passed as parameter when the component is implemented in a modal
    if (customOptionsService != null && isImplementingMoreFiltersCustomOptionsService(customOptionsService)) {
      this.customOptiosnService = customOptionsService;
    }

    this.customOptionsError = false;
    this.hasCustomOptions = false;

    this.showAllPropertyData = EntityType.Property === key && !filterForTableView;
    this.checkboxService = checkboxService ?? this.checkboxService;
    if (this.checkboxService) {
      this.checkboxService.getCheckboxState().subscribe(state => {
        this.checkboxState = state;
        this.showPropertyData = state.checked;
      });
    }
    this.customOptions$ =
      this.customOptiosnService?.getOptions?.().pipe(
        map(section => {
          this.hasCustomOptions = section != null;
          this.customOptionsError = section?.some(s => !!s.errorMessage);
          return this.transformSectionOptions(section);
        })
      ) || of(null);

    // The pinned values could be directly passed as calculated base on the pinned attribute
    pinnedFilters = pinnedFilters || this._getPinnedFilters(filtersCatalog);
    if (pinnedFilters?.length > 0) {
      this.pinnedFiltersCatalog = connectFilters(pinnedFilters, userFilters);
    }

    this.isCatalogData = isCatalogData;
    this.dialogOptions = options;
    this.isFilterDialog = isFilter;
    this.userFilters = userFilters;
    this.defaultColumns = defaultFilters;
    this.filtersCatalog = connectFilters(filtersCatalog, userFilters);
    this.forReset = userFilters.map(usrFilter => (usrFilter ? Object.assign({}, usrFilter) : undefined));
    this.fullCatalog = this.filtersCatalog.map(filters => (filters ? Object.assign({}, filters) : undefined));
    this.filterForRecords = filterForRecords;
    this.filterForTableView = filterForTableView;
    this.mainHeader = mainHeader;
    this.entity = key as EntityType;

    this.searchFormControl = this.formBuilder.control('');
    this.searchFormControl.valueChanges.subscribe(this._searchFilter);

    if (this.entity === EntityType.Property) {
      this.customOptions$.pipe(untilDestroyed(this)).subscribe(this._handleCustomOptionsChange);
    }
  }

  private transformSectionOptions(section: SectionOptionsSettings[]): SectionOptionsSettings[] {
    return section?.map(section => {
      if (section.sectionId === 'usagesSection') {
        return {
          ...section,
          options: section.options.map(option => ({
            ...option,
            value: getPropertyTypeLabel(option.value),
          })),
        };
      }
      return section;
    });
  }

  private _getPinnedFilters = (filtersCatalog: ISearchFilterSettings[]) => {
    // Iterate through the columns inside each section and get only the filters where the attribute pinned is true
    const pinnedFilters = filtersCatalog.map(section => {
      const filters = section.filters.filter(filter => filter.pinned);
      return { ...section, filters };
    });
    return pinnedFilters;
  };

  private _handleCustomOptionsChange = (options: SectionOptionsSettings[]): void => {
    const isUsageSelected = (usage: UsageTypes) => section => section.options.some(o => o.value === usage && o.checked);

    this.isIndustrialChecked = options?.some(isUsageSelected(UsageTypes.Industrial));
    this.isRetailChecked = options?.some(isUsageSelected(UsageTypes.Retail));
    this.isLandChecked = options?.some(isUsageSelected(UsageTypes.Land));

    this.filtersCatalog = this._filterSectionsByUsageSelections(this.fullCatalog);
  };

  /**
   * It filters out sections based on the usage selections (checked boxes of custom options).
   */
  private _filterSectionsByUsageSelections = (sections: ISearchFilterSettings[]): ISearchFilterSettings[] => {
    return sections.filter(section => {
      if (!this.hasCustomOptions) return true;
      if (!this.isIndustrialChecked && section.sectionId === PropertySections.IndustrialCharacteristics) return false;
      if (!this.isRetailChecked && section.sectionId === PropertySections.RetailCharacteristics) return false;
      if (!this.isLandChecked && section.sectionId === PropertySections.LandCharacteristics) return false;
      return true;
    });
  };

  public onCkboxToggle(checkbox: MatCheckbox, sectionId: number, title: string, filter: IOption) {
    if (this.isCheckboxDisabled(filter)) {
      return;
    }

    if (!checkbox.checked && this.isUserFiltersFull()) {
      this.onCheck(title, filter, sectionId);
    } else {
      this.onUncheck(title, filter);
    }

    if (!this.isModal) {
      this.customOptiosnService?.onApplyChanges?.();
      this.filtersChange.emit(this.userFilters);
    }
  }

  // Update the method that determines if a checkbox should be disabled
  public isCheckboxDisabled(filter: any): boolean {
    const baseDisabled = this.isCatalogData && filter.required;

    // Generic override logic
    if (this.overrideDisable != null) {
      return baseDisabled || this.overrideDisable?.({ filterKey: filter.key });
    }

    return baseDisabled;
  }

  public onRemove(title: string, removed: IOption) {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    userFilter.filters = userFilter.filters.filter(usrFilter => usrFilter !== removed);
    this.userFilters = cleanUserFilters(this.userFilters);
    removed['checked'] = false;
  }

  public onClear() {
    this.userFilters = [];
    this.filtersCatalog = this.filtersCatalog.map(catalogFilter => ({
      ...catalogFilter,
      filters: catalogFilter.filters.map(filter => ({ ...filter, checked: false })),
    }));
  }

  public onReset() {
    if (this.filterForTableView && (this.defaultColumns?.length || 0) > 0) {
      this.resetToDefaultColumns();
      return;
    }

    this.userFilters = this.forReset.map(usrFilter => Object.assign({}, usrFilter));
    this.filtersCatalog = connectFilters(this.data.filtersCatalog || [], this.userFilters);
  }

  public applySelections() {
    if (this.isModal) {
      // Filter out columns based on isIndustrialChecked, isRetailChecked, and isLandChecked
      const filteredUserFilters = this._filterSectionsByUsageSelections(this.userFilters);
      this.customOptiosnService?.onApplyChanges?.();
      this.filtersChange.emit(filteredUserFilters);
    }
  }

  #isPartOfGroupSelection = (checkedFltr: IOption) => checkedFltr.selectItemsInGroup && checkedFltr.group_key != null;

  #toggleGroupInFilters = (value: boolean, filter: IOption, title: string) => {
    const filterCatalog = this.filtersCatalog.find(usrFilter => usrFilter.title === title);
    filterCatalog.filters = filterCatalog.filters.map(f => {
      if (f.group_key === filter.group_key) {
        f.checked = value;
      }
      return f;
    });
  };

  #someGroupFilter = (filter: IOption, userFilter: ISearchFilterSettings) =>
    userFilter.filters.some(f => f.group_key === filter.group_key);

  #addFilterToSelectedSectionFilters(checkedFltr: IOption, userFilter: ISearchFilterSettings, title: string) {
    this.#toggleGroupInFilters(true, checkedFltr, title);
    if (!this.#someGroupFilter(checkedFltr, userFilter)) {
      userFilter.filters = [...userFilter.filters, checkedFltr].sort((a, b) => a.key - b.key);
    }
    return;
  }

  #handleGroupSelections(checkedFltr: IOption, userFilter: ISearchFilterSettings, title: string, sectionId: number) {
    if (userFilter) {
      this.#addFilterToSelectedSectionFilters(checkedFltr, userFilter, title);
    } else {
      this.userFilters.push({ title: title, filters: [checkedFltr], sectionId: sectionId });
      this.userFilters.sort((a, b) => (a.title < b.title ? -1 : 1));
      this.#toggleGroupInFilters(true, checkedFltr, title);
    }
  }

  private onCheck(title: string, checkedFltr: IOption, sectionId: number) {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    if (checkedFltr.checked) {
      checkedFltr.checked = true;
    } else {
      addCheckedProperty(checkedFltr);
    }

    if (this.#isPartOfGroupSelection(checkedFltr)) {
      this.#handleGroupSelections(checkedFltr, userFilter, title, sectionId);
      return;
    }

    if (userFilter) {
      userFilter.filters = [...userFilter.filters, checkedFltr].sort((a, b) => a.key - b.key);
    } else {
      this.userFilters.push({ title: title, filters: [checkedFltr], sectionId: sectionId });
      this.userFilters.sort((a, b) => (a.title < b.title ? -1 : 1));
    }
  }

  private onUncheck(title: string, uncheckedFltr: IOption) {
    const userFilter = this.userFilters.find(usrFilter => usrFilter.title === title);
    const isFilterNotEqual = f => f.key !== uncheckedFltr.key;
    let filterUnCheckedCallback = isFilterNotEqual;

    if (this.#isPartOfGroupSelection(uncheckedFltr)) {
      filterUnCheckedCallback = f => isFilterNotEqual(f) && f.group_key !== uncheckedFltr.group_key;
      this.#toggleGroupInFilters(false, uncheckedFltr, title);
    }

    userFilter.filters = userFilter.filters.filter(filterUnCheckedCallback);
    uncheckedFltr.checked = false;
    this.userFilters = cleanUserFilters(this.userFilters);
  }

  private isUserFiltersFull(): boolean {
    return !this.filterForRecords && this.userFilters.length > 0
      ? this.userFilters.map(usrFilter => usrFilter.filters).reduce((acc, val) => [...acc, ...val], []).length <
          MAX_NUM_FILTERS
      : true;
  }

  private _searchFilter = (termToSearch: string): void => {
    const filterBySearchTerm = (sections: ISearchFilterSettings[]) => {
      const filterByTerm = (filter: IOption) => filter.value.toUpperCase().includes(termToSearch.toUpperCase());
      return sections.map(section => ({ ...section, filters: section.filters.filter(filterByTerm) }));
    };

    let filtersSource = this.fullCatalog;
    if (this.isClipboard) {
      filtersSource = this._filterSectionsByUsageSelections(this.fullCatalog);
    }

    this.filtersCatalog = termToSearch !== '' ? filterBySearchTerm(filtersSource) : filtersSource;
    this.filtersCatalog = cleanUserFilters(this.filtersCatalog);
  };

  onCancel() {
    this.dialogRef.close();
  }

  resetToDefaultColumns() {
    this.userFilters = (this.defaultColumns || []).map(usrFilter => ({
      ...usrFilter,
      filters: [...(usrFilter?.filters || [])],
    }));
    this.filtersCatalog = connectFilters(this.data.filtersCatalog || this.fullCatalog || [], this.userFilters);
    if (!this.isModal) {
      this.customOptiosnService?.onApplyChanges?.();
      this.filtersChange.emit(this.userFilters);
    }

    this.customOptiosnService?.onResetClick?.();
  }

  ngOnDestroy() {
    this.userFilters = null;
    this.filtersCatalog = null;
  }
  onCheckboxChange(_event: any): void {
    this.showPropertyData = !this.showPropertyData;
    const checked = this.showPropertyData;
    this.checkboxState = { checked };
    if (this.checkboxService) {
      this.checkboxService.setCheckboxState({ checked });
    }
  }
}
